import { Form } from "@unform/web";
import { Building, Pencil, X } from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Input from "~/components/RegisterForm/Input";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { toast } from "react-toastify";
import api from "~/services/api";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { FullGridContext } from "../../..";
import { parseISO } from "date-fns";
import DatePicker from "~/components/RegisterForm/DatePicker";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import { Scope } from "@unform/core";
import Textarea from "~/components/RegisterForm/Textarea";
import { AlertContext } from "~/App";
import FindGeneric from "~/components/Finds/FindGeneric";

export const InputContext = createContext({});

const discountTypeOptions = [
  { value: "percent", label: "Percent (%)" },
  { value: "value", label: "Value ($)" },
];

export default function Compensation({
  access,
  id,
  defaultFormType = "preview",
  selected,
  handleOpened,
}) {
  const { alertBox } = useContext(AlertContext);
  const { successfullyUpdated, setSuccessfullyUpdated } =
    useContext(FullGridContext);
  const [pageData, setPageData] = useState({
    loaded: true,
    discount: 0,
    discount_type: "percent",
    consolidated_amount: 0,
  });
  const [loading, setLoading] = useState(false);

  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");

  const generalForm = useRef();

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  function getTotalBalance(receivables) {
    return (receivables || []).reduce((acc, curr) => acc + curr.balance, 0);
  }

  function getDiscountAmount(totalBalance, discount, discountType) {
    const value = parseFloat(discount) || 0;
    if (discountType === "percent") {
      return (totalBalance * value) / 100;
    }
    return value;
  }

  function getConsolidatedAmount(receivables, discount, discountType) {
    const totalBalance = getTotalBalance(receivables);
    const discountAmount = getDiscountAmount(
      totalBalance,
      discount,
      discountType
    );
    return Math.max(totalBalance - discountAmount, 0);
  }

  // Rateia o desconto total proporcionalmente ao balance de cada receivable.
  // O resíduo de arredondamento é absorvido pela última parcela para que a
  // soma dos descontos individuais seja exatamente igual ao desconto total.
  function getProratedDiscounts(receivables, discount, discountType) {
    const list = receivables || [];
    const totalBalance = getTotalBalance(list);
    const totalDiscount = Math.min(
      getDiscountAmount(totalBalance, discount, discountType),
      totalBalance
    );

    if (totalBalance <= 0 || totalDiscount <= 0) {
      return list.map(() => 0);
    }

    let allocated = 0;
    return list.map((receivable, index) => {
      if (index === list.length - 1) {
        const rest = Math.max(totalDiscount - allocated, 0);
        return Number(rest.toFixed(2));
      }
      const share = Number(
        ((receivable.balance / totalBalance) * totalDiscount).toFixed(2)
      );
      allocated += share;
      return share;
    });
  }

  function recalc(nextDiscount, nextDiscountType) {
    const discount = nextDiscount ?? pageData.discount;
    const discount_type = nextDiscountType ?? pageData.discount_type;
    setPageData((prev) => ({
      ...prev,
      discount,
      discount_type,
      consolidated_amount: getConsolidatedAmount(
        prev.receivables,
        discount,
        discount_type
      ).toFixed(2),
    }));
  }

  async function handleGeneralFormSubmit(data) {
    setLoading(true);
    async function handleCompensation(submitData) {
      const sortedReceivables = [...pageData.receivables].sort((a, b) =>
        a.due_date > b.due_date ? 1 : -1
      );
      const proratedDiscounts = getProratedDiscounts(
        sortedReceivables,
        submitData.discount,
        submitData.discount_type
      );
      await api
        .post(`/receivables/compensation`, {
          receivables: sortedReceivables.map((receivable, index) => ({
            id: receivable.id,
            prorated_discount: proratedDiscounts[index] || 0,
          })),
          discount: parseFloat(submitData.discount) || 0,
          discount_type: submitData.discount_type,
          paymentMethod: submitData.paymentMethod,
          memo: submitData.memo,
          send_invoice: submitData.send_invoice,
        })
        .then(({ data: response }) => {
          if (response.payment_page_url) {
            toast("Consolidation created. Payment link generated.", {
              autoClose: 2000,
            });
          } else {
            toast("Consolidation created.", { autoClose: 1500 });
          }
          handleOpened(null);
          setLoading(false);
        })
        .catch((err) => {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
          setLoading(false);
        });
    }
    alertBox({
      title: "Attention!",
      descriptionHTML:
        "Would you like to send the consolidated invoice to the student?",
      buttons: [
        {
          title: "No",
          class: "cancel",
          onPress: async () => {
            await handleCompensation({ ...data, send_invoice: false });
            handleOpened(null);
          },
        },
        {
          title: "Yes",
          onPress: async () => {
            await handleCompensation({ ...data, send_invoice: true });
            handleOpened(null);
          },
        },
      ],
    });
    return;
  }

  useEffect(() => {
    async function loadData() {
      const promises = [];

      selected.map((receivable) => {
        promises.push(
          api
            .get(`/receivables/${receivable.id}`)
            .then(({ data }) => {
              return data;
            })
            .catch((err) => {
              console.log(err);
            })
        );
      });
      Promise.all(promises).then((data) => {
        if (data.length === 0) {
          return;
        }
        const { student } = data[0].issuer;
        setPageData((prev) => ({
          ...prev,
          receivables: data,
          loaded: true,
          consolidated_amount: getConsolidatedAmount(
            data,
            prev.discount,
            prev.discount_type
          ).toFixed(2),
          student: {
            ...student,
            searchFields: {
              processtype_id: student.processtype_id,
              processsubstatus_id: student.processsubstatus_id,
              filial_id: student.filial_id,
            },
          },
        }));
      });
    }
    loadData();
  }, []);

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        formType === "preview" ? (
          <div className="border h-full rounded-xl overflow-hidden flex flex-col justify-start gap-1 overflow-y-scroll">
            <div className="relative bg-gray-100 h-16 px-4 py-2 flex flex-row items-start justify-start">
              <button
                onClick={() => setFormType("full")}
                className="absolute top-2 right-20 text-md font-bold bg-mila_orange text-white rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <Pencil size={16} color="#fff" /> Open
              </button>
              <button
                onClick={() => handleOpened(null)}
                className="absolute top-2 right-2 text-md font-bold bg-secondary rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <X size={16} /> Close
              </button>
              <h2 style={{ fontSize: 24 }}>{pageData.name}</h2>
            </div>
          </div>
        ) : (
          <div className="flex h-full flex-row items-start justify-between gap-4">
            <div className="flex flex-col items-center justify-between text-xs w-32 gap-4">
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="general"
              >
                <Building size={16} /> General
              </RegisterFormMenu>
            </div>
            <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
              <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
                <Form
                  ref={generalForm}
                  onSubmit={handleGeneralFormSubmit}
                  className="w-full"
                >
                  <InputContext.Provider
                    value={{
                      id,
                      generalForm,
                      setSuccessfullyUpdated,
                      fullscreen,
                      setFullscreen,
                      successfullyUpdated,
                      handleCloseForm,
                    }}
                  >
                    {id === "new" || pageData.loaded ? (
                      <>
                        <FormHeader
                          access={access}
                          title={
                            "Consolidation - " +
                            pageData.student?.name +
                            " " +
                            pageData.student?.last_name
                          }
                          registry={registry}
                          InputContext={InputContext}
                          loading={loading}
                        />

                        <InputLineGroup
                          title="GENERAL"
                          activeMenu={activeMenu === "general"}
                        >
                          {pageData.receivables && (
                            <>
                              <InputLine title="Consolidation Data">
                                <Input
                                  type="text"
                                  name="qty_of_receivables"
                                  shrink
                                  readOnly
                                  title="Qty. of Receivables"
                                  defaultValue={pageData.receivables.length}
                                  InputContext={InputContext}
                                />
                                <Input
                                  type="text"
                                  name="total_amount"
                                  shrink
                                  readOnly
                                  title="Total Balance"
                                  defaultValue={getTotalBalance(
                                    pageData.receivables
                                  ).toFixed(2)}
                                  InputContext={InputContext}
                                />
                                <SelectPopover
                                  name="discount_type"
                                  title="Discount Type"
                                  isSearchable={false}
                                  defaultValue={discountTypeOptions.find(
                                    (option) =>
                                      option.value === pageData.discount_type
                                  )}
                                  options={discountTypeOptions}
                                  onChange={(option) =>
                                    recalc(undefined, option.value)
                                  }
                                  InputContext={InputContext}
                                />
                                <Input
                                  type="number"
                                  name="discount"
                                  required
                                  title="Discount"
                                  defaultValue={pageData.discount}
                                  onChange={(value) =>
                                    recalc(value, undefined)
                                  }
                                  InputContext={InputContext}
                                />
                                <Input
                                  type="text"
                                  name="consolidated_amount"
                                  readOnly
                                  title="Consolidated Amount"
                                  value={pageData.consolidated_amount}
                                  defaultValue={pageData.consolidated_amount}
                                  InputContext={InputContext}
                                />
                              </InputLine>
                              <FindGeneric
                                route="paymentmethods"
                                title="Payment Methods"
                                scope="paymentMethod"
                                type="Inbounds"
                                InputContext={InputContext}
                                defaultValue={{
                                  id: pageData.receivables[0].paymentMethod?.id,
                                  description:
                                    pageData.receivables[0].paymentMethod
                                      ?.description,
                                  platform:
                                    pageData.receivables[0].paymentMethod
                                      ?.platform,
                                }}
                                fields={[
                                  {
                                    title: "Description",
                                    name: "description",
                                  },
                                  {
                                    title: "Platform",
                                    name: "platform",
                                  },
                                ]}
                              />
                              <InputLine>
                                <Textarea
                                  name="memo"
                                  title="Memo"
                                  grow
                                  rows={3}
                                  InputContext={InputContext}
                                />
                              </InputLine>

                              {(() => {
                                const sortedReceivables = [
                                  ...pageData.receivables,
                                ].sort((a, b) =>
                                  a.due_date > b.due_date ? 1 : -1
                                );
                                const proratedDiscounts = getProratedDiscounts(
                                  sortedReceivables,
                                  pageData.discount,
                                  pageData.discount_type
                                );
                                return sortedReceivables.map(
                                  (receivable, index) => {
                                    const proratedDiscount =
                                      proratedDiscounts[index] || 0;
                                    const netBalance = Math.max(
                                      receivable.balance - proratedDiscount,
                                      0
                                    );
                                    return (
                                    <InputLine
                                      key={index}
                                      title={index === 0 ? "Receivables" : ""}
                                    >
                                      <p className="pt-3">#{index + 1}</p>
                                      <Scope path={`receivables[${index}]`}>
                                        <Input
                                          type="hidden"
                                          name="id"
                                          defaultValue={receivable.id}
                                          InputContext={InputContext}
                                        />
                                        <Input
                                          type="text"
                                          name="memo"
                                          grow
                                          readOnly
                                          title="Memo"
                                          defaultValue={receivable.memo}
                                          InputContext={InputContext}
                                        />
                                        <Input
                                          type="text"
                                          name="total"
                                          grow
                                          readOnly
                                          title="Total Amount"
                                          defaultValue={receivable.total}
                                          InputContext={InputContext}
                                        />
                                        <Input
                                          type="text"
                                          name="balance"
                                          grow
                                          readOnly
                                          title="Balance Amount"
                                          defaultValue={receivable.balance.toFixed(
                                            2
                                          )}
                                          InputContext={InputContext}
                                        />
                                        <Input
                                          type="text"
                                          name="prorated_discount"
                                          grow
                                          readOnly
                                          title="Discount (prorated)"
                                          value={proratedDiscount.toFixed(2)}
                                          defaultValue={proratedDiscount.toFixed(
                                            2
                                          )}
                                          InputContext={InputContext}
                                        />
                                        <Input
                                          type="text"
                                          name="net_balance"
                                          grow
                                          readOnly
                                          title="Net Balance"
                                          value={netBalance.toFixed(2)}
                                          defaultValue={netBalance.toFixed(2)}
                                          InputContext={InputContext}
                                        />
                                        <DatePicker
                                          name="due_date"
                                          grow
                                          disabled
                                          title="Due Date"
                                          defaultValue={parseISO(
                                            receivable.due_date
                                          )}
                                          placeholderText="MM/DD/YYYY"
                                          InputContext={InputContext}
                                        />
                                        <Input
                                          type="text"
                                          name="status"
                                          grow
                                          readOnly
                                          title="Status"
                                          defaultValue={receivable.status}
                                          InputContext={InputContext}
                                        />
                                      </Scope>
                                    </InputLine>
                                    );
                                  }
                                );
                              })()}
                            </>
                          )}
                        </InputLineGroup>
                      </>
                    ) : (
                      <FormLoading />
                    )}
                  </InputContext.Provider>
                </Form>
              </div>
            </div>
          </div>
        )
      ) : null}
    </Preview>
  );
}
